import CapMaterialTable from "components/inventory/CapMaterialTable";
import CapMaterialsTable from "components/inventory/CapMaterialsTable";
import AddQuantityModal from "components/inventory/AddQuantityModal";
import InventoryActivityLogModal from "components/inventory/InventoryActivityLogModal";
import { useFetchInventoryQuery, useUpdateInventoryMutation, useCreateInventoryMutation, useFetchInventoryActivityLogQuery } from "features/store";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { useFetchInventoryFilterOptionsQuery } from "features/store";
import { Button, Tooltip, message, Space } from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";

const MaterialsStock = () => {
  const { user } = useSelector((state) => state.user);
  const userRole = user?.roll || user?.role || (user?.user && (user.user.roll || user.user.role)) || (JSON.parse(localStorage.getItem("user") || "{}").roll);
  const isViewer = userRole === "viewer";
  // State to manage pagination for CAP MATERIAL and OTHER MATERIAL tables
  const [paginationState, setPaginationState] = useState({
    "CAP MATERIAL": {
      current: 1,
      pageSize: 10,
    },
    "OTHER MATERIAL": {
      current: 1,
      pageSize: 10,
    },
  });

  // State to manage modal visibility
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [activityLogModal, setActivityLogModal] = useState({
    visible: false,
    inventoryId: null,
  });

  const [updateInventory, { isLoading: isUpdating }] = useUpdateInventoryMutation();
  const [createInventory, { isLoading: isCreating }] = useCreateInventoryMutation();

  // Fetch activity log data
  const {
    data: activityLogData,
    isLoading: isActivityLogLoading,
  } = useFetchInventoryActivityLogQuery(activityLogModal.inventoryId || 0, {
    skip: !activityLogModal.visible || !activityLogModal.inventoryId,
  });

  // Handler to update pagination for a specific table
  const handlePaginationChange = (materialType, page, pageSize) => {
    setPaginationState((prev) => ({
      ...prev,
      [materialType]: {
        current: page,
        pageSize: pageSize || prev[materialType].pageSize,
      },
    }));
  };

  // Handler to open modal
  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  // Handler to close modal
  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Handler to open update modal
  const handleOpenUpdateModal = () => {
    setIsUpdateModalOpen(true);
  };

  // Handler to close update modal
  const handleCloseUpdateModal = () => {
    setIsUpdateModalOpen(false);
  };

  // Handler for activity log click
  const handleActivityLogClick = (inventoryId) => {
    setActivityLogModal({ visible: true, inventoryId });
  };

  // Handler to close activity log modal
  const handleCloseActivityLogModal = () => {
    setActivityLogModal({ visible: false, inventoryId: null });
  };

  // Handler for modal OK button (Add)
  const handleModalOk = async (formData) => {
    try {
      // If inventory exists, update it; otherwise create new
      if (formData.id || formData.inventoryId) {
        await updateInventory(formData).unwrap();
        message.success('Quantity added successfully!');
      } else {
        const result = await createInventory(formData).unwrap();
        // Handle multiple items response
        if (result.items && Array.isArray(result.items)) {
          message.success(`Successfully created ${result.items.length} inventory item(s)!`);
        } else if (result.created_items && Array.isArray(result.created_items)) {
          message.success(`Successfully created ${result.created_items.length} inventory item(s)!`);
        } else {
          message.success('Inventory created successfully!');
        }
      }
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error saving inventory:', error);
      message.error(error?.data?.message || error?.data?.error || 'Failed to save inventory. Please try again.');
    }
  };

  // Handler for update modal OK button
  const handleUpdateModalOk = async (formData) => {
    try {
      // Update mode - must have inventory ID
      if (formData.id || formData.inventoryId) {
        await updateInventory(formData).unwrap();
        message.success('Inventory updated successfully!');
        setIsUpdateModalOpen(false);
      } else {
        message.error('Please select an inventory item to update');
      }
    } catch (error) {
      console.error('Error updating inventory:', error);
      message.error(error?.data?.message || error?.data?.error || 'Failed to update inventory. Please try again.');
    }
  };

  const currentPage = paginationState["OTHER MATERIAL"]?.current || 1;
  const pageSize = paginationState["OTHER MATERIAL"]?.pageSize || 10;

  const [otherMaterialFilters, setOtherMaterialFilters] = useState({
    other_material_name: null,
  });

  const { data: dataSource, isLoading: loading } = useFetchInventoryQuery({
    type: "OTHER MATERIAL",
    page: currentPage,
    page_size: pageSize,
    ...(otherMaterialFilters?.other_material_name
      ? { other_material_name: otherMaterialFilters.other_material_name }
      : {}),
    // cap_material:"CARD BOARD"
  });

  const { data: otherMaterialFilterOptions } = useFetchInventoryFilterOptionsQuery({
    type: "OTHER MATERIAL",
  });

  // Handle paginated response structure
  const safeDataSource = Array.isArray(dataSource) ? dataSource : (dataSource?.results || []);
  const totalCount = dataSource?.count || (Array.isArray(dataSource) ? dataSource.length : 0);

  return (
    <Style>
      <div className="material-div">
        <div className="add-button-container">
          {!isViewer && (
            <Space size="middle">
              <Tooltip title="add quantity">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size="large"
                  onClick={handleOpenModal}
                >
                  ADD
                </Button>
              </Tooltip>
              <Tooltip title="update quantity">
                <Button
                  type="default"
                  icon={<EditOutlined />}
                  size="large"
                  onClick={handleOpenUpdateModal}
                >
                  UPDATE
                </Button>
              </Tooltip>
            </Space>
          )}
        </div>
        <CapMaterialTable
          dataSource={safeDataSource}
          loading={loading}
          filterOptions={otherMaterialFilterOptions}
          otherMaterialPagination={paginationState["OTHER MATERIAL"]}
          onOtherMaterialPaginationChange={(page, pageSize) =>
            handlePaginationChange("OTHER MATERIAL", page, pageSize)
          }
          onOtherMaterialFiltersChange={setOtherMaterialFilters}
          otherMaterialTotalCount={totalCount}
          capMaterialPagination={paginationState["CAP MATERIAL"]}
          onCapMaterialPaginationChange={(page, pageSize) =>
            handlePaginationChange("CAP MATERIAL", page, pageSize)
          }
          onActivityLogClick={handleActivityLogClick}
        />
      </div>
      <AddQuantityModal
        open={isModalOpen}
        onCancel={handleCloseModal}
        onOk={handleModalOk}
        loading={isUpdating || isCreating}
        materialType="CAP MATERIAL"
      />
      <AddQuantityModal
        open={isUpdateModalOpen}
        onCancel={handleCloseUpdateModal}
        onOk={handleUpdateModalOk}
        loading={isUpdating}
        isUpdateMode={true}
        materialType="CAP MATERIAL"
      />
      <InventoryActivityLogModal
        open={activityLogModal.visible}
        onCancel={handleCloseActivityLogModal}
        inventoryId={activityLogModal.inventoryId}
        activityData={activityLogData}
        loading={isActivityLogLoading}
      />
    </Style>
  );
};

export default MaterialsStock;

const Style = styled.div`
  .add-button-container {
    margin: 2rem 2rem 1rem 2rem;
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }

  .material-div {
    margin: 0;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
  }
`;

